import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DateTime } from 'luxon';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { HeaderMainNav } from 'Src/components/navigation/HeaderMainNav';
import { CashBankEditCloseBank } from 'Src/feature/cash-bank/CashBankEditCloseBank';
import { CashBankEditClosingAmount } from 'Src/feature/cash-bank/CashBankEditClosingAmount';
import CashBankEditDeposit from 'Src/feature/cash-bank/CashBankEditDeposit';
import CashBankEditStartingAmount from 'Src/feature/cash-bank/CashBankEditStartingAmount';
import CashBankEditWithdrawal from 'Src/feature/cash-bank/CashBankEditWithdrawal';
import CashBankLog from 'Src/feature/cash-bank/CashBankLog';
import { useCashBank } from 'Src/feature/cash-bank/useCashBank';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { PageMainHeader } from 'Src/feature/page/PageMainHeader';
import { PagePanelHeader } from 'Src/feature/page/PagePanelHeader';
import { useLocation } from 'Src/feature/provision/useLocation';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import useRoute from 'Src/routes/useRoute';

import { useOnErrorShowToast } from '../toasts/useOnErrorShowToast';
import { useTrainingMode } from '../training-mode/useTrainingMode';

import styles from './CashBankView.module.scss';

const sectionId = 'CashBankView';

const CashBankView: FC = () => {
  const { t } = useTranslation();
  const { crumbs, navigate } = useRoute();
  const { data: currentCashBank } = useCashBank.query.current();
  const { mutate } = useCashBank.mutate.reopenOne(useOnErrorShowToast({ title: t('cashBank.errors.reopenBank') }));
  const handlePrintError = useOnErrorShowToast({ title: t('cashBank.errors.printReport') });
  const { mutate: printReport } = useCashBank.mutate.printReport(handlePrintError);
  const employeeGetAllMap = useEmployee.query.map();
  const terminal = useTerminal.query.get();
  const locationsWithTerminals = useLocation.query.getAll({ orgId: terminal.data?.location.orgId as number });
  const trainingMode = useTrainingMode();

  const onCloseClick = () => navigate(crumbs.pos.cashBankList.main.path);
  const onReopenBank = () => mutate({ cashBankId: currentCashBank?.id as number });
  const onOpenNewBank = () => navigate(crumbs.pos.cashBankList.mainOpenBank.path);

  const isOpen = currentCashBank?.status === 'Open';

  const employee = currentCashBank?.employeeId ? employeeGetAllMap.data?.get(currentCashBank?.employeeId) : undefined;

  return (
    <Page
      data-testid={sectionId}
      panel={(
        <>
          <PagePanelHeader>
            <CloseIconButton onClick={onCloseClick} />
            <Copy color="textPrimary" variant="h6" value={t('cashBank.label')} />
          </PagePanelHeader>
          {isOpen ? (
            <Flex className={classNames(styles.padding, styles.fullHeight)}>
              <Flex className={styles.fullHeight} justifyContent="flex-end" grow gap="sm">
                <Copy
                  data-testid={`${sectionId}-open-created-at`}
                  variant="body"
                  value={t('cashBank.labels.bankStartedAt', {
                    time: currentCashBank?.createdAt ? DateTime.fromMillis(currentCashBank.createdAt).toLocaleString(DateTime.TIME_SIMPLE) : 'N/A',
                  })}
                />
                <Copy variant="body" size="sm" value={locationsWithTerminals.data?.find((l) => l.id === currentCashBank?.locationId)?.name} />
                <Copy variant="body" size="sm" value={employee ? t('labels.util.employeeFullName', employee) : undefined} />
                <Copy data-testid={`${sectionId}-label-starting-amount`} variant="body" value={t('cashBank.labels.startingAmount')} />
                <Copy data-testid={`${sectionId}-open-amount`} variant="body" size="sm" type="currency" value={currentCashBank?.openAmount} />
              </Flex>
              <Flex shrink gap="sm">
                <CashBankEditStartingAmount />
                <CashBankLog />
              </Flex>
            </Flex>
          ) : (
            <Flex className={classNames(styles.padding, styles.fullHeight)}>
              <Flex className={styles.fullHeight} justifyContent="flex-end" grow gap="sm">
                <Copy variant="body" size="sm" value={locationsWithTerminals.data?.find((l) => l.id === currentCashBank?.locationId)?.name} />
                <Copy variant="body" size="sm" value={employee ? t('labels.util.employeeFullName', employee) : undefined} />
                <Copy data-testid={`${sectionId}-label-starting-amount`} variant="body" value={t('cashBank.labels.startingAmount')} />
                <Copy data-testid={`${sectionId}-open-amount`} variant="body" size="sm" type="currency" value={currentCashBank?.openAmount} />
                <Copy data-testid={`${sectionId}-label-closing-amount`} variant="body" value={t('cashBank.labels.closingAmount')} />
                <Copy data-testid={`${sectionId}-close-amount`} variant="body" size="sm" type="currency" value={currentCashBank?.closeAmount} />
              </Flex>
              <Flex shrink gap="sm">
                <CashBankEditClosingAmount />
                <CashBankLog />
              </Flex>
            </Flex>
          )}
        </>
      )}
      main={(
        <PageMain
          header={<PageMainHeader end={<HeaderMainNav />} />}
          main={(
            <Flex alignItems="center" justifyContent="center" className={styles.fullHeight}>
              { isOpen ? (
                <Flex>
                  <Copy variant="body" align="center" value={t('cashBank.info.bankOpen')} />
                  <CashBankEditDeposit />
                  <CashBankEditWithdrawal />
                  <CashBankEditCloseBank onOpenNewBank={onOpenNewBank} />
                </Flex>
              ) : (
                <Flex>
                  <Copy variant="body" align="center" value={t('cashBank.labels.bankClosed')} />
                  <CTA data-testid={`${sectionId}-reprint-report`} label={t('cashBank.labels.reprintReport')} onClick={() => void printReport({ cashBankId: currentCashBank?.id as number })} />
                  <CTA data-testid={`${sectionId}-reopen-bank`} label={t('cashBank.labels.reopenClosedBank')} onClick={onReopenBank} disabled={trainingMode.isTraining}/>
                </Flex>
              )}
            </Flex>
          )}
        />
      )}
    />
  );
};

export default CashBankView;
export { sectionId };
